/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import PropTypes from 'prop-types';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import get from 'lodash/get';
import compose from 'lodash/fp/compose';
import defaultsDeep from 'lodash/defaultsDeep';
import { withNamespaces } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import { Container, Row, Column } from '../../../styles/Grid';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import {
  buildImagePath,
  parseUrlConfigParams,
} from '../../../utils/cloudinary';
import { CLOUDINARY_BASE_VALUES } from '../../../utils/constants';
import {
  bottomFooterSectionCss,
  generalLinkCss,
  generalLinkContainerCss,
  generalLinksCss,
  socialMediaIcons,
  socialMediaIconContainerCss,
  socialMediaIconLinkCss,
  languageSelectorCss,
  bottomFooterContentCss,
  generalLinkHoverCss,
  selectorFlagCss,
} from './index.styles';

const socialSizeConfig = parseUrlConfigParams({
  ...CLOUDINARY_BASE_VALUES,
  gravity: 'auto:subject',
  height: 18,
  width: 18,
}).config;

const flagSizeConfig = parseUrlConfigParams({
  ...CLOUDINARY_BASE_VALUES,
  width: 20,
}).config;

const defaultData = {
  GeneralLinks: { value: [] },
  LanguageSelectorCta: {
    value: {
      href: '',
      text: '',
    },
  },
  LanguageSelector: {
    value: {
      name: '',
      code: '',
      flag: '',
    },
  },
  SocialMedia: { value: [] },
};

export class LowerFooterLinks extends SafePureComponent {
  breadcrumbsScrollContainer = React.createRef();
  fields = defaultsDeep(get(this, 'props.fields'), defaultData);

  state = {
    displayLeftShadow: false,
    displayRightShadow: false,
    accordionStates: {},
    jsonFields: {},
    stickyBarOffset: 0,
  };

  /**
   * Creates an object in relation to the argument's value,
   * setting the target an rel props if true
   * @param {bool} isAnExternalLink
   * @returns {Object}
   */
  getExternalLinkAttributes(isAnExternalLink) {
    return isAnExternalLink
      ? {
          target: '_blank',
          rel: 'noopener noreferrer',
        }
      : {};
  }

  onLanguageSelected = () => {
    // eslint-disable-next-line no-undef
    localStorage.setItem('redirectedPage', window.location.pathname);
    return true;
  };

  safeRender() {
    const { fields } = this;
    const language = get(this.props.sitecoreContext, 'route.itemLanguage', '');
    const { GeneralLinks, SocialMedia, LanguageSelector } = fields;
    const { t } = this.props;
    const languageName = get(LanguageSelector, 'value.name', '');
    const flagUrl = get(LanguageSelector, 'value.flag', '');
    const languageSelectorUrl = get(
      fields,
      'LanguageSelectorCta.value.href',
      '/language-selector',
    );

    return (
      <div css={bottomFooterSectionCss}>
        <Container>
          <Row>
            <Column cols={{ sm: 6 }}>
              <div css={bottomFooterContentCss}>
                <div css={generalLinksCss}>
                  {get(GeneralLinks, 'value', []).map((link, index) => {
                    const isAnExternalLink = link.target === '_blank';
                    return (
                      <div
                        key={`general-lower-link-${index}`}
                        css={generalLinkContainerCss}
                      >
                        {isEmpty(link.href) ? (
                          <span css={generalLinkCss}>{link.text}</span>
                        ) : (
                          <a
                            {...this.getExternalLinkAttributes(
                              isAnExternalLink,
                            )}
                            css={[generalLinkCss, generalLinkHoverCss]}
                            href={link.href}
                            aria-label={link.text}
                          >
                            {link.text}
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div css={socialMediaIcons}>
                  {get(SocialMedia, 'value', []).map((socialFields, index) => (
                    <div
                      key={`social-media-lower-${index}`}
                      css={socialMediaIconContainerCss}
                    >
                      <a
                        css={socialMediaIconLinkCss}
                        href={get(socialFields, 'link.value.href')}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        <img
                          alt={
                            get(socialFields, 'image.value.alt') ||
                            get(socialFields, 'link.value.text')
                          }
                          src={buildImagePath(
                            socialSizeConfig,
                            get(socialFields, 'image.value.src'),
                          )}
                        />
                      </a>
                    </div>
                  ))}
                </div>
                {language && (
                  <a
                    css={languageSelectorCss}
                    href={languageSelectorUrl}
                    onClick={() => this.onLanguageSelected()}
                  >
                    <span css={selectorFlagCss}>
                      <img
                        src={buildImagePath(flagSizeConfig, flagUrl)}
                        alt={languageName}
                      />
                    </span>
                    <span>{t(`${languageName}Title`)}</span>
                  </a>
                )}
              </div>
            </Column>
          </Row>
        </Container>
      </div>
    );
  }
}

LowerFooterLinks.displayName = 'LowerFooterLinks';

const arrayType = {
  value: PropTypes.array,
};

LowerFooterLinks.defaultProps = {
  fields: PropTypes.shape({
    GeneralLinks: arrayType,
    LanguageSelectorCta: PropTypes.shape({
      value: PropTypes.shape({
        href: PropTypes.string,
        text: PropTypes.string,
      }),
    }),
    LanguageSelector: PropTypes.shape({
      value: PropTypes.shape({
        name: PropTypes.string,
        code: PropTypes.string,
        flag: PropTypes.string,
      }),
    }),
    SocialMedia: arrayType,
  }),
  t: key => key,
};

LowerFooterLinks.displayName = 'LowerFooterLinks';

export default compose(
  withNamespaces(),
  withSitecoreContext(),
)(LowerFooterLinks);
